import type { JsonValue } from "../types/geojson";

export type EditableGeometryType = "Point" | "LineString" | "Polygon";
export type PropertyControlType = "text" | "color" | "number" | "json" | "select";

export interface PropertyFieldDef {
  key: string;
  label: string;
  control: PropertyControlType;
  defaultValue?: JsonValue;
  help: string;
  min?: number;
  max?: number;
  step?: number;
  options?: string[];
  aliases?: string[];
  placeholder?: string;
}

export const ORIGINAL_DRAWER_DEFAULTS = {
  Polygon: {
    fillColor: "#555555",
    fillOpacity: 0.2,
    strokeColor: "#000000",
    strokeOpacity: 1,
    strokeWidth: 1,
    zIndex: 1
  },
  LineString: {
    strokeColor: "#555555",
    strokeOpacity: 1,
    strokeWidth: 2,
    zIndex: 2
  },
  Point: {
    icon: {
      path: "./icons/default-marker.svg"
    },
    label: null,
    zIndex: 3,
    color: "#dc2626",
    radius: 6
  }
} as const;

export const FEATURE_PROPERTY_SCHEMA: Record<EditableGeometryType, PropertyFieldDef[]> = {
  Polygon: [
    {
      key: "fillColor",
      label: "Fill color",
      control: "color",
      defaultValue: ORIGINAL_DRAWER_DEFAULTS.Polygon.fillColor,
      aliases: ["fill"],
      help: "Polygon interior color. The short-form GeoJSON property fill is also accepted on import."
    },
    {
      key: "fillOpacity",
      label: "Fill opacity",
      control: "number",
      defaultValue: ORIGINAL_DRAWER_DEFAULTS.Polygon.fillOpacity,
      min: 0,
      max: 1,
      step: 0.05,
      help: "Polygon interior opacity. Use a number from 0 to 1. Original default: 0.2."
    },
    {
      key: "strokeColor",
      label: "Stroke color",
      control: "color",
      defaultValue: ORIGINAL_DRAWER_DEFAULTS.Polygon.strokeColor,
      aliases: ["stroke"],
      help: "Polygon border color. The short-form GeoJSON property stroke is also accepted on import."
    },
    {
      key: "strokeOpacity",
      label: "Stroke opacity",
      control: "number",
      defaultValue: ORIGINAL_DRAWER_DEFAULTS.Polygon.strokeOpacity,
      min: 0,
      max: 1,
      step: 0.05,
      help: "Polygon border opacity. Use a number from 0 to 1. Original default: 1."
    },
    {
      key: "strokeWidth",
      label: "Stroke width",
      control: "number",
      defaultValue: ORIGINAL_DRAWER_DEFAULTS.Polygon.strokeWidth,
      min: 0,
      step: 1,
      aliases: ["strokeWeight"],
      help: "Polygon border width in pixels. The original docs used strokeWeight; this rebuild accepts both strokeWidth and strokeWeight."
    },
    {
      key: "zIndex",
      label: "Z index",
      control: "number",
      defaultValue: ORIGINAL_DRAWER_DEFAULTS.Polygon.zIndex,
      step: 1,
      help: "Stacking order for overlapping features. Higher values appear above lower values where supported by the renderer."
    }
  ],
  LineString: [
    {
      key: "strokeColor",
      label: "Stroke color",
      control: "color",
      defaultValue: ORIGINAL_DRAWER_DEFAULTS.LineString.strokeColor,
      aliases: ["stroke"],
      help: "Line color. The short-form GeoJSON property stroke is also accepted on import."
    },
    {
      key: "strokeOpacity",
      label: "Stroke opacity",
      control: "number",
      defaultValue: ORIGINAL_DRAWER_DEFAULTS.LineString.strokeOpacity,
      min: 0,
      max: 1,
      step: 0.05,
      help: "Line opacity. Use a number from 0 to 1. Original default: 1."
    },
    {
      key: "strokeWidth",
      label: "Stroke width",
      control: "number",
      defaultValue: ORIGINAL_DRAWER_DEFAULTS.LineString.strokeWidth,
      min: 0,
      step: 1,
      aliases: ["strokeWeight"],
      help: "Line width in pixels. The original docs used strokeWeight; this rebuild accepts both strokeWidth and strokeWeight."
    },
    {
      key: "zIndex",
      label: "Z index",
      control: "number",
      defaultValue: ORIGINAL_DRAWER_DEFAULTS.LineString.zIndex,
      step: 1,
      help: "Stacking order for overlapping features."
    }
  ],
  Point: [
    {
      key: "label",
      label: "Label",
      control: "json",
      defaultValue: ORIGINAL_DRAWER_DEFAULTS.Point.label,
      placeholder: '{"text":"Cairo","color":"#111827","fontWeight":"bold","fontSize":"12px"}',
      help: "Optional marker label. The Drawer provides separate fields for text, color, font weight, and font size."
    },
    {
      key: "icon",
      label: "Icon URL",
      control: "json",
      defaultValue: ORIGINAL_DRAWER_DEFAULTS.Point.icon,
      placeholder: '"./icons/Map_icons_by_Scott_de_Jonge_mosque.svg"',
      help: "Optional marker icon URL. Leave empty to use the default marker."
    },
    {
      key: "color",
      label: "Fallback circle color",
      control: "color",
      defaultValue: ORIGINAL_DRAWER_DEFAULTS.Point.color,
      aliases: ["fillColor", "fill"],
      help: "MapLibre fallback circle color used when no renderable icon URL is available."
    },
    {
      key: "radius",
      label: "Fallback circle radius",
      control: "number",
      defaultValue: ORIGINAL_DRAWER_DEFAULTS.Point.radius,
      min: 1,
      step: 1,
      help: "MapLibre fallback circle radius in pixels."
    },
    {
      key: "zIndex",
      label: "Z index",
      control: "number",
      defaultValue: ORIGINAL_DRAWER_DEFAULTS.Point.zIndex,
      step: 1,
      help: "Stacking order for overlapping markers where supported by the renderer."
    }
  ]
};

export function getPropertySchema(geometryType: EditableGeometryType): PropertyFieldDef[] {
  return FEATURE_PROPERTY_SCHEMA[geometryType];
}

export function standardPropertyKeys(geometryType: EditableGeometryType): Set<string> {
  const keys = new Set<string>(["name"]);
  for (const field of getPropertySchema(geometryType)) {
    keys.add(field.key);
    for (const alias of field.aliases ?? []) keys.add(alias);
  }
  return keys;
}

export function coerceStandardProperty(field: PropertyFieldDef, rawValue: string): JsonValue | undefined {
  const trimmed = rawValue.trim();
  if (trimmed === "") return undefined;

  if (field.control === "number") {
    const numeric = Number(trimmed);
    if (!Number.isFinite(numeric)) throw new Error(`${field.key} must be a number.`);
    if (field.min !== undefined && numeric < field.min) throw new Error(`${field.key} must be at least ${field.min}.`);
    if (field.max !== undefined && numeric > field.max) throw new Error(`${field.key} must be at most ${field.max}.`);
    return numeric;
  }

  if (field.control === "color") {
    if (!/^#[0-9a-f]{6}$/i.test(trimmed)) throw new Error(`${field.key} must be a hex color such as #555555.`);
    return trimmed;
  }

  if (field.control === "json") {
    if (trimmed === "null") return null;
    if ((trimmed.startsWith("{") && trimmed.endsWith("}")) || (trimmed.startsWith("[") && trimmed.endsWith("]")) || (trimmed.startsWith('"') && trimmed.endsWith('"'))) {
      const parsed = JSON.parse(trimmed) as unknown;
      if (isJsonValue(parsed)) return parsed;
    }
    return trimmed;
  }

  return rawValue;
}

function isJsonValue(value: unknown): value is JsonValue {
  if (value === null) return true;
  if (["string", "number", "boolean"].includes(typeof value)) return true;
  if (Array.isArray(value)) return value.every((item) => isJsonValue(item));
  if (typeof value === "object") return Object.values(value as Record<string, unknown>).every((item) => isJsonValue(item));
  return false;
}
